/**
 * GMB Website Service
 * Handles fetching GMB data and creating websites with real business data
 */

import GMBService from './GMBService';
import GMBDataMapper from './GMBDataMapper';
import GoogleAdsKeywordService from './GoogleAdsKeywordService';
import logger from '../utils/logger';
class GMBWebsiteService {
  /**
   * Fetch user's GMB profile data and map it to template format
   * @param {number} templateId - Template ID (1-8)
   * @param {string} accessToken - Optional Google access token
   * @returns {Promise<Object>} Mapped template data with real GMB data
   */
  async createWebsiteWithGMBData(templateId, opts = {}) {
    try {
      const accessToken = opts?.accessToken || null;
      const desiredLocationId = opts?.locationId || null;
      logger.log(`[GMBWebsiteService] Creating website with template ${templateId}`, { desiredLocationId: desiredLocationId || '(first location)' });
      
      // Step 1: Get GMB accounts
      const accounts = await GMBService.getAccounts(accessToken);
      if (!accounts || accounts.length === 0) {
        throw new Error('No GMB accounts found. Please connect your Google My Business account first.');
      }

      logger.log(`[GMBWebsiteService] Found ${accounts.length} GMB accounts`);
      
      // Step 2: Find the requested location (or first) across all accounts
      let accountName = accounts[0].name;
      let primaryLocation = null;

      for (const acct of accounts) {
        const locs = await GMBService.getLocations(accessToken, acct.name);
        if (!Array.isArray(locs) || locs.length === 0) continue;
        if (desiredLocationId) {
          const match = locs.find(l => String(l.name || '').split('/').pop() === String(desiredLocationId));
          if (match) { primaryLocation = match; accountName = acct.name; break; }
        }
        if (!primaryLocation) {
          primaryLocation = locs[0];
          accountName = acct.name;
        }
      }
      if (!primaryLocation) {
        throw new Error('No business locations found in your GMB account.');
      }
      
      // Step 4: Get additional data for the location
      const locationId = this.extractLocationId(primaryLocation.name);
      const accountId = this.extractAccountId(accountName);
      let reviews = null;
      let attributes = null;
      let photos = null;
      let services = null;
      
      try {
        // Try to get reviews (optional)
        reviews = await GMBService.getReviews(accessToken, accountName, primaryLocation.name);
        logger.log(`[GMBWebsiteService] Fetched ${reviews?.reviews?.length || 0} reviews`);
      } catch (error) {
        logger.warn('[GMBWebsiteService] Could not fetch reviews:', error.message);
      }
      
      try {
        // Try to get attributes (optional)
        attributes = await GMBService.getLocationAttributes(locationId, accessToken);
        logger.log(`[GMBWebsiteService] Fetched ${attributes?.length || 0} attributes`);
      } catch (error) {
        logger.warn('[GMBWebsiteService] Could not fetch attributes:', error.message);
      }

      try {
        // Try to get photos (optional)
        photos = await GMBService.getPhotos(accessToken, accountId, locationId, 20);
        logger.log(`[GMBWebsiteService] Fetched ${photos?.length || 0} photos`);
      } catch (error) {
        logger.warn('[GMBWebsiteService] Could not fetch photos:', error.message);
      }

      try {
        // Try to get services via Business Information serviceItems (preferred)
        services = await GMBService.getServiceItems(locationId, accessToken);
        logger.log(`[GMBWebsiteService] Fetched ${services?.length || 0} services`);
      } catch (error) {
        logger.warn('[GMBWebsiteService] Could not fetch services:', error.message);
      }

      // Try to get GMB posts/feed
      let posts = null;
      try {
        posts = await GMBService.getLocalPosts(accessToken, accountId, locationId);
        logger.log(`[GMBWebsiteService] Fetched ${posts?.length || 0} GMB posts`);
      } catch (error) {
        logger.warn('[GMBWebsiteService] Could not fetch GMB posts:', error.message);
      }

      // Try to get keyword suggestions (same as competitor analysis suggestions)
      let keywordsData = [];
      try {
        const businessCategory = GoogleAdsKeywordService.extractBusinessCategory(primaryLocation);
        const locationString = GoogleAdsKeywordService.extractLocation(primaryLocation);
        keywordsData = GoogleAdsKeywordService.getEnhancedMockKeywords(businessCategory, locationString);
        logger.log(`[GMBWebsiteService] Generated ${keywordsData?.length || 0} keyword suggestions for category: ${businessCategory}`);
      } catch (error) {
        logger.warn('[GMBWebsiteService] Could not fetch keywords:', error.message);
      }

      // Step 5: Combine all GMB data
      const gmbData = {
        account: accounts.find(a => a.name === accountName) || accounts[0],
        location: primaryLocation,
        reviews: reviews,
        attributes: attributes,
        photos: photos,
        services: services,
        posts: posts,
        keywordsData: keywordsData,
        locationId: locationId,
        accountId: accountId
      };

      logger.log('[GMBWebsiteService] GMB data structure:', {
        hasAccount: !!gmbData.account,
        hasLocation: !!gmbData.location,
        hasReviews: !!gmbData.reviews,
        hasAttributes: !!gmbData.attributes,
        hasPhotos: !!gmbData.photos,
        hasServices: !!gmbData.services,
        hasPosts: !!gmbData.posts,
        hasKeywords: !!gmbData.keywordsData?.length,
        photoCount: gmbData.photos?.length || 0,
        serviceCount: gmbData.services?.length || 0,
        postCount: gmbData.posts?.length || 0,
        keywordsCount: gmbData.keywordsData?.length || 0,
        locationTitle: gmbData.location?.title,
        businessName: gmbData.location?.title
      });

      // Step 6: Map GMB data to template format
      const templateData = GMBDataMapper.mapGMBDataToTemplate(gmbData, templateId);
      
      logger.log('[GMBWebsiteService] Successfully mapped GMB data to template format');
      
      return {
        success: true,
        templateData: templateData,
        gmbData: gmbData,
        templateId: templateId
      };

    } catch (error) {
      logger.error('[GMBWebsiteService] Error creating website with GMB data:', error);
      
      // Return error with fallback to mock data
      return {
        success: false,
        error: error.message,
        templateData: null,
        fallbackToMock: true
      };
    }
  }

  /**
   * Extract location ID from location name
   * @param {string} locationName - e.g., "accounts/123/locations/456"
   * @returns {string} Location ID
   */
  extractLocationId(locationName) {
    if (!locationName) return null;
    const parts = locationName.split('/');
    return parts[parts.length - 1];
  }

  /**
   * Extract account ID from account name
   * @param {string} accountName - e.g., "accounts/123"
   * @returns {string} Account ID
   */
  extractAccountId(accountName) {
    if (!accountName) return null;
    const parts = accountName.split('/');
    return parts[parts.length - 1];
  }

  /**
   * Get user's primary GMB location (for quick access)
   * @param {string} accessToken - Optional Google access token
   * @returns {Promise<Object>} Primary location data
   */
  async getPrimaryLocation(accessToken = null) {
    try {
      const accounts = await GMBService.getAccounts(accessToken);
      if (!accounts || accounts.length === 0) {
        throw new Error('No GMB accounts found');
      }

      const locations = await GMBService.getLocations(accessToken, accounts[0].name);
      if (!locations || locations.length === 0) {
        throw new Error('No locations found');
      }

      return locations[0];
    } catch (error) {
      logger.error('[GMBWebsiteService] Error getting primary location:', error);
      throw error;
    }
  }

  /**
   * Check if user has GMB access
   * @param {string} accessToken - Optional Google access token
   * @returns {Promise<boolean>} Whether user has GMB access
   */
  async hasGMBAccess(accessToken = null) {
    try {
      const accounts = await GMBService.getAccounts(accessToken);
      return accounts && accounts.length > 0;
    } catch (error) {
      logger.error('[GMBWebsiteService] Error checking GMB access:', error);
      return false;
    }
  }

  /**
   * Get business categories from GMB data
   * @param {Object} gmbData - GMB data object
   * @returns {Array} Business categories
   */
  getBusinessCategories(gmbData) {
    const location = gmbData.location || {};
    return location.categories || [];
  }

  /**
   * Get business contact info from GMB data
   * @param {Object} gmbData - GMB data object
   * @returns {Object} Contact information
   */
  getContactInfo(gmbData) {
    const location = gmbData.location || {};
    const address = location.storefrontAddress || {};
    const phoneNumbers = location.phoneNumbers || {};

    return {
      businessName: location.title,
      address: address,
      phone: phoneNumbers.primaryPhone,
      website: location.websiteUri,
      hours: location.regularHours
    };
  }
}

export default new GMBWebsiteService();
